import React, { useEffect, useRef, useState } from 'react';
import { Dropdown, FormControl, FormGroup, FormLabel } from 'react-bootstrap';
import styled from 'styled-components';

import useDebounce from '../../hooks/useDebounce';
import { AutocompleteOption } from '../../interfaces/autocompleteOption';

interface BaseProps<T> extends Omit<React.HTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'onSelect'> {
  onSelect: (value: T) => void;
  onFail?: (err: any) => void;
  onChange?: (value: string) => void;
  overflow?: boolean;
  formLabel?: string;
  required?: boolean;
  displayLabelAsValue?: boolean;
  placeholder?: string;
  autoFocusWithoutList?: boolean;
  disabled?: boolean;
  offset?: number[];
  showNoItemsInfo: boolean;
  allowCustomValue?: boolean;
  value?: string;
}

interface AsyncProps<T> extends BaseProps<T> {
  optionsAsync?: (value: string) => Promise<AutocompleteOption<T>[]>;
}

interface StaticProps<T> extends BaseProps<T> {
  options?: AutocompleteOption<T>[];
}

type Props<T> = StaticProps<T> & AsyncProps<T>;

interface DropdownItemProps {
  onClick: () => void;
  label: string;
  sublabel?: string;
  highlightedLabel?: React.ReactNode;
}

type Suggestion = {
  value: string;
  label: string;
  sublabel?: string;
  id: string | number;
};

const StyledDropdownToggle = styled('div')`
  visibility: hidden;
  height: 0;
  width: 0;
  offset: none;
  border: none;
  appearance: none;

  .hdn-events-filter--license-plate__dropdown-toggle {
    background-image: none;
    &:focus {
      box-shadow: none;
    }
    &::after {
      content: none;
    }
  }
`;

function DropdownItem(props: DropdownItemProps) {
  const { sublabel, onClick, label, highlightedLabel } = props;
  const displayLabel = highlightedLabel || label;
  
  return sublabel ? (
    <Dropdown.Item onClick={onClick}>
      <div>{displayLabel}</div>
      <div>{sublabel}</div>
    </Dropdown.Item>
  ) : (
    <Dropdown.Item onClick={onClick}>{displayLabel}</Dropdown.Item>
  );
}

function LastDropdownItem(props) {
  const { showNoItemsInfo, suggestions, handleSugestion, value } = props;

  if (suggestions.length === 0 && showNoItemsInfo) {
    return <Dropdown.Item>There are no items matching your search.</Dropdown.Item>;
  }
  if (suggestions.length === 0) {
    return (
      <Dropdown.Item onClick={handleSugestion(value)} disabled={!value}>
        Custom (please enter)
      </Dropdown.Item>
    );
  }
}

export function Autocomplete(props: Props<any>) {
  const {
    options,
    optionsAsync,
    overflow,
    formLabel,
    required,
    onChange,
    onSelect,
    autoFocusWithoutList,
    onFail,
    disabled,
    displayLabelAsValue,
    placeholder = 'Select or start typing',
    showNoItemsInfo = true,
    allowCustomValue = false,
    value: externalValue,
    ...rest
  } = props;
  const [value, setValue] = useState<string>('');

  const [suggestions, setSuggestions] = useState<AutocompleteOption<any>[]>([]);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [dropdownShow, setDropdownShow] = useState<boolean>(false);
  const toggleRef = useRef();
  const valueDebounced = useDebounce(value, 500);

  const handleTextInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value: valueFromTarget } = e.target;
    setValue(valueFromTarget);
    if (valueFromTarget) {
      setDropdownShow(true);
    }
  };

  const selectSuggestion = (option: string | Suggestion) => {
    onSelect(option);

    setValue('');

    setDropdownShow(false);
  };

  const handleSugestion = (v) => () => {
    return selectSuggestion(v);
  };

  const handleEnterKey = (evt: KeyboardEvent) => {
    const target = evt.target as HTMLInputElement; // Type assertion to HTMLInputElement
    if (allowCustomValue && evt.key === 'Enter' && target.classList.contains('custom-autocomplete')) {
      if (target.value) {
        selectSuggestion(target.value);
        evt.target?.blur?.(); // blur the input to close the dropdown

      }
    }

    if (evt.key === 'Enter' && suggestions.length > 0 && target.classList.contains('custom-autocomplete')) {
      const suggestion = suggestions[0];
      // FIX AND TODO - check this function in whole app

      if (!dropdownShow) {
        return;
      }

      const hasPropertyValue = !!Object.keys(suggestion).find((s) => s === 'value');

      if (hasPropertyValue) {
        selectSuggestion(suggestion.value);
      } else {
        selectSuggestion(suggestion);
      }
      evt.target?.blur?.(); // blur the input to close the dropdown

      evt.stopPropagation();
      evt.preventDefault();
    }

  };

  useEffect(() => {
    if (onChange) {
      onChange(value);
    }
  }, [value]);

  useEffect(() => {
    if (optionsAsync) {
      if (valueDebounced) {
        optionsAsync(valueDebounced).then(
          (suggestionsFromResponse) => {
            setSuggestions(suggestionsFromResponse);
          },
          (err) => {
            if (onFail) {
              onFail(err);
            }
          },
        );
      } else {
        setSuggestions([]); // clear suggestions
      }
    }
  }, [valueDebounced, optionsAsync]);

  useEffect(() => {
    // implement partial search by name
    if (!options) return;
    // regexp to escape all special chars
    const escapeRegexp = /[-[\]{}()*+?.,\\^$|#\s]/g;

    const escaped = value.replace(escapeRegexp, '');

    const regexp = new RegExp(String.raw`\b${escaped.split('').join('+?.*')}`, 'i');

    const regexpedSugestions: any[] = value
      ? options.filter((option: any) => regexp.test(option.label) || regexp.test(JSON.stringify(option.value)))
      : options;
    setSuggestions(regexpedSugestions);
  }, [value, options]);

  useEffect(() => {
    window.addEventListener('keyup', handleEnterKey);
    return () => window.removeEventListener('keyup', handleEnterKey);
  });

  const displayedValue = React.useMemo(() => {
    if (displayLabelAsValue) {
      return options?.find((item) => item.value === value)?.label;
    }
    return value;
  }, [value]);

  const onFocus = () => {
    setIsFocused(true);
    if (autoFocusWithoutList) {
      setDropdownShow(false);
    } else {
      setDropdownShow(true);
    }
  };

  const onClick = () => {
    if (autoFocusWithoutList) {
      setDropdownShow(true);
    }
  };

  const onBlur = () => {
    setIsFocused(false);
    setTimeout(() => {
      setDropdownShow(false);
      if (!allowCustomValue) {
        setValue('');
      }
    }, 200);
  };

  const highlightText = (text: string, searchValue: string) => {
    if (!searchValue) return text;
    
    const escapeRegexp = /[-[\]{}()*+?.,\\^$|#\s]/g;
    const escaped = searchValue.replace(escapeRegexp, '');
    
    if (!escaped) return text;
    
    const regexp = new RegExp(`(${escaped.split('').join('+?.*')})`, 'gi');
    const parts = text.split(regexp);
    
    return parts.map((part, index) => {
      if (regexp.test(part)) {
        return <strong key={index}>{part}</strong>;
      }
      return part;
    });
  };

  return (
    <FormGroup>
      {formLabel && <FormLabel>{formLabel}</FormLabel>}
      <FormControl
        className="hdn-input custom-autocomplete"
        autoFocus={autoFocusWithoutList}
        onClick={onClick}
        value={displayedValue || (isFocused ? '' : externalValue)}
        required={required}
        onChange={handleTextInput}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        onKeyDown={(e) => {
          return e.key === 'Enter' && e.preventDefault();
        }}
        {...rest}
      />
      <Dropdown show={dropdownShow}>
        <StyledDropdownToggle>
          <Dropdown.Toggle
            variant="link"
            id="dropdown-basic"
            className="hdn-events-filter--license-plate__dropdown-toggle"
            ref={toggleRef}
          />
        </StyledDropdownToggle>
        <Dropdown.Menu
          className="hdn-events-filter--license-plate__dropdown"
          style={overflow && { overflowY: 'scroll', maxHeight: '300px' }}
          popperConfig={{
            strategy: 'fixed',
            modifiers: [
              {
                name: 'offset',
                options: { offset: props.offset || [-300, 0] },
              },
              {
                name: 'flip',
                enabled: true,
                options: {
                  fallbackPlacements: ['top', 'bottom', 'left', 'right'],
                },
              },
              {
                name: 'preventOverflow',
                enabled: true,
                options: {
                  boundary: 'viewport',
                  padding: 8,
                },
              },
            ],
          }}
        >
          {suggestions.map((suggestion: Suggestion) => (
            <DropdownItem
              sublabel={suggestion.sublabel}
              label={suggestion.label}
              highlightedLabel={highlightText(suggestion.label, value)}
              key={suggestion.id}
              onClick={handleSugestion(suggestion.value)}
            />
          ))}

          <LastDropdownItem suggestions={suggestions} value={value} showNoItemsInfo={showNoItemsInfo} handleSugestion={handleSugestion} />
        </Dropdown.Menu>
      </Dropdown>
    </FormGroup>
  );
}
